import { useMerchant } from "@/app/providers/merchantProvider";
import CryIcon from "@/components/_icons/cryIcon";
import { OrderType, ServiceType } from "@/constants/enum";
import { ScheduledTime } from "@/dto/scheduledTime";
import { Service } from "@/dto/service";
import { cloverColorTheme } from "@/utils/cloverColorTheme";
import pom from "@/utils/pom";
import {
  getFromSessionStorage,
  saveToSessionStorage,
} from "@/utils/sessionStorageUtils";
import { LocalStorageKey, SessionStorageKey } from "@/utils/storageKeys";
import { applyDataTestId } from "@/utils/testUtils";
import { useTranslations } from "next-intl";
import { useId, useMemo } from "react";
import { ConfirmButton } from "../button/confirmButton";
import { TextButton } from "../button/textButton";
import { RegularModal } from "../modals/modals";
import useMediaQuery from "@/utils/useMediaQuery";
import clsx from "clsx";

interface DeliveryModalErrorProps {
  isOpen: boolean;
  onRequestClose: () => void;
  address: any;
  onEnterNewAddress: () => void;
  checkoutCloseHandler: () => void;
}

export const DeliveryModalError = ({
  isOpen,
  onRequestClose,
  address,
  onEnterNewAddress,
  checkoutCloseHandler,
}: DeliveryModalErrorProps) => {
  const { merchant } = useMerchant();
  const { services } = merchant;
  const t = useTranslations("DeliveryModalError");
  const titleId = useId();
  const subtitleId = useId();
  const isMobileScreen = useMediaQuery("(max-width: 640px)");

  const merchantColor =
    merchant.merchantMeta?.colorPalette?.color ||
    cloverColorTheme.Main.primaryColor;

  const switchToPickup = () => {
    const sessionTime = getFromSessionStorage<Partial<ScheduledTime>>(
      SessionStorageKey.SCHEDULED_TIME,
      {}
    );
    const merchantService = merchant?.services.find(
      (s: Service) =>
        s.type === ServiceType.PICKUP || s.type === ServiceType.WEBSITE
    );
    const deliveryService = merchant?.services.find(
      (s: Service) => s.type === ServiceType.DELIVERY
    );
    let enabledDefaultOrderType = "";
    const retailServices = [
      { type: OrderType.PICKUP, service: merchantService },
      { type: OrderType.DELIVERY, service: deliveryService },
    ];
    const enabledServices = retailServices.filter(
      ({ service }) => service?.enabled
    );

    if (enabledServices.length >= 1) {
      enabledDefaultOrderType = enabledServices[0].type;
    }
    saveToSessionStorage(SessionStorageKey.SCHEDULED_TIME, {
      orderType: merchant?.isRetail
        ? enabledDefaultOrderType
        : OrderType.PICKUP,
      date: sessionTime?.date || null,
      time: sessionTime?.time || null,
    });

    if (merchant?.isRetail || merchant?.isRestaurant) {
      window.dispatchEvent(
        new StorageEvent("storage", { key: LocalStorageKey.PICKUP_ORDER_TYPE })
      );
    }
    checkoutCloseHandler();
    onRequestClose?.();
  };

  const isOnlyDeliveryEnabled = useMemo(() => {
    if (!services || services.length === 0) {
      return false;
    }

    let deliveryFoundAndEnabled = false;

    for (const service of services) {
      if (service.enabled) {
        if (service.type !== "DELIVERY") {
          return false;
        }
        deliveryFoundAndEnabled = true;
      }
    }
    return deliveryFoundAndEnabled;
  }, [services]);

  return (
    <RegularModal
      isOpen={isOpen}
      onRequestClose={() => {
        if (!isOnlyDeliveryEnabled) {
          switchToPickup();
        }
        onRequestClose();
      }}
      overlayClassName="bg-opacity-50"
      contentClassName="bg-white sm:rounded-lg shadow-lg w-[100%] h-[100%] sm:h-[auto] sm:w-[70%] mx-auto overflow-hidden pt-6"
      contentLabel={t("dialogLabel")}
      focusElementId={titleId}
    >
      <div
        data-testid={applyDataTestId(pom.modals.deliveryErrorModal)}
        className="px-4 pb-4 flex flex-col justify-center items-center"
      >
        <CryIcon brandingColor={merchantColor} />
        <h2
          id={titleId}
          tabIndex={-1}
          className="mt-4 text-3xl font-medium text-center text-[#373737] text-[32px]"
        >
          {t("title")}
          {!isMobileScreen && (
            <span id={subtitleId} className="sr-only">
              {`${t("cannotDeliver", { merchantName: merchant.name })}.
              ${address.address1}, ${address.city}, ${address.state}, ${
                address.zip
              }.
              ${
                !isOnlyDeliveryEnabled
                  ? t("chooseDifferentAddress")
                  : t("chooseDifferentDeliveryAddress")
              }`}
            </span>
          )}
        </h2>
        <p className="mt-4 text-base leading-6 text-center text-[15px] text-neutral-700 w-full">
          {t("cannotDeliver", { merchantName: merchant.name })}
        </p>
        <p className="text-base font-bold leading-6 text-center text-[15px] text-neutral-700 w-full">
          {address.address1}, {address.city}, {address.state}, {address.zip}
        </p>
        <p className="mt-4 text-base leading-6 text-center text-[15px] text-neutral-700 w-full">
          {!isOnlyDeliveryEnabled
            ? t("chooseDifferentAddress")
            : t("chooseDifferentDeliveryAddress")}
        </p>

        {!isOnlyDeliveryEnabled && (
          <ConfirmButton
            onClick={switchToPickup}
            className="px-16 py-4 mt-24 max-w-full text-xl font-medium leading-7 text-center text-white whitespace-nowrap bg-[#228800] disabled:bg-[#C7C7C7] rounded-lg w-[250px] max-md:px-5 max-md:mt-10"
          >
            <p>{t("switchToPickup")}</p>
          </ConfirmButton>
        )}

        <TextButton
          className={clsx(
            "fixed sm:static bottom-16 border-t border-[#e5e5e5] sm:border-none sm:mt-4 sm:mb-4 text-xl font-medium leading-7 text-center max-md:mt-10",
            { "w-full h-16 rounded-none": isMobileScreen }
          )}
          onClick={onEnterNewAddress}
          style={{ color: merchantColor }}
        >
          <p>{t("enterNewAddress")}</p>
        </TextButton>
      </div>
    </RegularModal>
  );
};
